import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { AiDisclaimerDialogComponent } from '../../components/dialogs/ai-disclaimer-dialog/ai-disclaimer-dialog.component';

@Injectable({ providedIn: 'root' })
export class AiDisclaimerService {
  private openDialogRef: MatDialogRef<AiDisclaimerDialogComponent> | null = null;
  private openAlgorithmId: string | null = null;

  constructor(private readonly dialog: MatDialog) {}

  hasAccepted(algorithmId: string): boolean {
    return this._getAccepted(algorithmId);
  }

  accept(algorithmId: string): void {
    this._setAccepted(algorithmId, true);
  }

  getDisclaimerAcceptedState(analyst: { id?: string; type?: string } | null | undefined): boolean {
    if (!analyst?.id || analyst.type !== 'algorithm') {
      return true;
    }

    return this.hasAccepted(analyst.id);
  }

  ensureDisclaimerAccepted(analyst: { id?: string; type?: string } | null | undefined): Observable<boolean> {
    if (!analyst?.id || analyst.type !== 'algorithm') {
      return of(true);
    }

    if (this.hasAccepted(analyst.id)) {
      return of(true);
    }

    return this.promptAcceptance(analyst.id);
  }

  promptAcceptance(algorithmId: string): Observable<boolean> {
    if (!algorithmId || this.hasAccepted(algorithmId)) {
      return of(true);
    }

    if (this.openDialogRef && this.openAlgorithmId === algorithmId) {
      return this.openDialogRef.afterClosed().pipe(map((accepted) => accepted === true));
    }

    this.openDialogRef?.close(false);

    this.openAlgorithmId = algorithmId;
    this.openDialogRef = this.dialog.open(AiDisclaimerDialogComponent, {
      disableClose: true,
      width: '580px',
    });

    return this.openDialogRef.afterClosed().pipe(
      tap(() => {
        this.openDialogRef = null;
        this.openAlgorithmId = null;
      }),
      map((accepted) => {
        if (accepted === true) {
          this.accept(algorithmId);
          return true;
        }
        return false;
      })
    );
  }

  private _getAccepted(algorithmId: string): boolean {
    return localStorage.getItem(`ai_disclaimer_accepted_${algorithmId}`) === 'true';
  }

  private _setAccepted(algorithmId: string, value: boolean): void {
    localStorage.setItem(`ai_disclaimer_accepted_${algorithmId}`, value.toString());
  }
}
